import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ProfileService } from 'src/app/services/profile.service';
import { Profile } from 'src/app/shared/models/register.models';

@Component({
  selector: 'fun-register-identity-verification',
  templateUrl: './register-identity-verification.component.html',
  styleUrls: ['./register-identity-verification.component.scss']
})
export class RegisterIdentityVerificationComponent implements OnInit, OnDestroy {

  @Input() profile: Profile;
  @Output() public finishEvent = new EventEmitter<any>();

  step = 0;
  loading = false;
  iframeLoaded = false;

  urlSafeCategorize: SafeResourceUrl;
  urlSafeIdentityVerification: SafeResourceUrl;

  text: string;
  image: number;
  btnList = [
    {
      type: 'primaryDark',
      index: 1,
      title: this.translate.instant('register.messages.continue'),
    }
  ];

  // Cada paso (categorize / identityVerification) registra su propio listener y se
  // desregistra en cuanto lo consume, para que un postMessage duplicado o tardío del
  // paso anterior no pueda confundirse con la validación del paso siguiente.
  private categorizeMessageHandler = (e: MessageEvent) => this.handleCategorizeMessage(e);
  private identityVerificationMessageHandler = (e: MessageEvent) => this.handleIdentityVerificationMessage(e);

  constructor(
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private translate: TranslateService,
    private profileService: ProfileService,
  ) { }

  ngOnInit() {
    if (this.profile?.mangopayIdvSession?.status === 'VALIDATED') {
      this.finish();
      return;
    }

    this.setInitialMessageParams();
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.categorizeMessageHandler);
    window.removeEventListener('message', this.identityVerificationMessageHandler);
  }

  private handleCategorizeMessage(e: MessageEvent) {
    if ((e.data as any)?.func !== 'control_status') { return; }
    window.removeEventListener('message', this.categorizeMessageHandler);
    const status = (e.data as any).data.value.controlStatus;
    const validated = status === 'VALIDATED';
    this.loading = true;
    this.profileService.saveCategorizeResult(validated).subscribe(() => {
      if (validated) {
        this.fetchIdentityVerification();
      } else {
        this.setErrorMessageParams();
        this.loading = false;
      }
      this.cdRef.detectChanges();
    });
    this.cdRef.detectChanges();
  }

  private handleIdentityVerificationMessage(e: MessageEvent) {
    if ((e.data as any)?.func !== 'control_status') { return; }
    window.removeEventListener('message', this.identityVerificationMessageHandler);
    const status = (e.data as any).data.value.controlStatus;
    if (status === 'VALIDATED') {
      this.finish();
    } else if (!status) {
      // MangoPay no siempre añade controlStatus a la vuelta de este paso: sin dato no
      // podemos afirmar que haya fallado, así que no mostramos un error.
      this.setPendingMessageParams();
    } else {
      this.setErrorMessageParams();
    }
    this.cdRef.detectChanges();
  }

  start() {
    this.loading = true;
    this.iframeLoaded = false;
    this.cdRef.detectChanges();
    if (this.profile && this.profile.isEnrolledSca) {
      this.fetchIdentityVerification();
    } else {
      window.addEventListener('message', this.categorizeMessageHandler);
      this.callCategorize();
    }
  }

  private callCategorize() {
    forkJoin([
      this.profileService.categorizeUser().pipe(
        catchError(() => timer(2000).pipe(switchMap(() => this.profileService.categorizeUser())))
      ),
      timer(500)
    ]).pipe(map(([resp]) => resp)).subscribe(resp => {
      const isCompany = this.profile && this.profile.profileType === 2;
      if (resp === 'Categorized' || (isCompany && !resp.returnUrl)) {
        this.fetchIdentityVerification();
      } else if (!resp.returnUrl) {
        timer(2000).subscribe(() => this.callCategorize());
      } else {
        this.urlSafeCategorize = this.sanitizer.bypassSecurityTrustResourceUrl(resp.returnUrl);
        this.loading = false;
        this.step = 1;
      }
      this.cdRef.detectChanges();
    }, () => {
      this.setErrorMessageParams();
      this.loading = false;
      this.cdRef.detectChanges();
    });
  }

  fetchIdentityVerification() {
    this.loading = true;
    this.iframeLoaded = false;
    this.cdRef.detectChanges();
    window.addEventListener('message', this.identityVerificationMessageHandler);
    this.callIdentityVerification();
  }

  private callIdentityVerification() {
    forkJoin([
      this.profileService.getIdentityVerification().pipe(
        catchError(() => timer(2000).pipe(switchMap(() => this.profileService.getIdentityVerification())))
      ),
      timer(500)
    ]).pipe(map(([resp]) => resp)).subscribe(resp => {
      if (!resp.hostedUrl) {
        timer(2000).subscribe(() => this.callIdentityVerification());
      } else {
        this.urlSafeIdentityVerification = this.sanitizer.bypassSecurityTrustResourceUrl(resp.hostedUrl);
        this.loading = false;
        this.step = 2;
      }
      this.cdRef.detectChanges();
    }, () => {
      this.setErrorMessageParams();
      this.loading = false;
      this.cdRef.detectChanges();
    });
  }

  charged(event) {
    setTimeout(() => {
      this.iframeLoaded = true;
      this.cdRef.detectChanges();
    }, 1000);
    this.cdRef.detectChanges();
  }

  setInitialMessageParams() {
    this.image = 3;
    this.text = this.translate.instant('register.identity_verification.txt');
    this.btnList = [
      {
        type: 'primaryDark',
        index: 1,
        title: this.translate.instant('register.messages.continue'),
      }
    ];
    this.cdRef.detectChanges();
  }

  setErrorMessageParams() {
    this.step = 3;
    this.iframeLoaded = false;
    this.image = 1;
    this.text = this.translate.instant('SCA.ISSUE');
    this.btnList = [
      {
        type: 'primaryDark',
        index: 1,
        title: this.translate.instant('register.messages.continue'),
      }
    ];
    this.cdRef.detectChanges();
  }

  setPendingMessageParams() {
    this.step = 4;
    this.iframeLoaded = false;
    this.image = 6;
    this.text = this.translate.instant('register.identity_verification.pending');
    this.btnList = [
      {
        type: 'primaryDark',
        index: 1,
        title: this.translate.instant('register.messages.continue'),
      }
    ];
    this.cdRef.detectChanges();
  }

  retry() {
    window.removeEventListener('message', this.categorizeMessageHandler);
    window.removeEventListener('message', this.identityVerificationMessageHandler);
    this.step = 0;
    this.urlSafeCategorize = null;
    this.urlSafeIdentityVerification = null;
    this.setInitialMessageParams();
    this.cdRef.detectChanges();
  }

  onBtnEvent() {
    if (this.step === 3) {
      this.retry();
    } else {
      this.start();
    }
  }

  finish() {
    this.finishEvent.emit();
    this.cdRef.detectChanges();
  }

}
